import {
  PaginatedResponseApplication,
  Application as ApplicationDto,
  CreatePrivateApplicationResponse as CreatePrivateApplicationResponseDTo,
  AppUserAccountsResponse as AppUserAccountsResponseDto,
  AppDevicesResponse as AppDevicesResponseDto,
  AccountStatusInElectric,
  EmployeeStatusEnum,
} from '@electricjs/core_entity-client';
import { ApiListResponseDto } from '@externalTypes/general';
import { omit } from 'lodash';

import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import {
  Application,
  ApplicationCreationArgs,
  ApplicationFetchingArgs,
  ApplicationIconResponse,
  ApplicationIconUploadArgs,
  ApplicationUpdateArgs,
  AppDevicesResponse,
  AppUserAccountsResponse,
  CreatePrivateApplicationResponse,
  GetAppDevicesArgs,
  GetAppUserAccountsArgs,
  ListApplicationsArgs,
  ListApplicationsResponse,
  ListPrivateApplicationsArgs,
  PrivateApplicationCreationArgs,
  PrivateApplicationIconUploadArgs,
} from '@/types/applications';
import {
  organizationApiSlice,
  transformOrganizationApplication,
} from './organizationApiSlice';
import { FetchBaseQueryError, QueryReturnValue } from '@reduxjs/toolkit/query';

export const transformApplication = (
  application: ApplicationDto
): Application => {
  return {
    id: application.id,
    name: application.name,
    icon: application.icon,
    slug: application.slug,
    organizationId: application.organization_id,
    url: application.url,
    userManagementUrl: application.user_management_url,
    integrationAvailability: application.integration_availability,
    category: application.category,
  };
};

export const applicationApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getApplications: builder.query<
      ListApplicationsResponse,
      ListApplicationsArgs
    >({
      providesTags: [TagTypes.Application],
      query: ({
        name,
        limit,
        offset,
        orderBy,
        sortOrder,
        integrationAvailability: integrationAvailabilityFilter,
      }) => {
        const params = new URLSearchParams();

        limit && params.append('limit', String(limit));
        offset && params.append('offset', String(offset));
        name && params.append('name', String(name));
        orderBy && params.append('orderBy', String(orderBy));
        sortOrder && params.append('sortOrder', String(sortOrder));

        // Adding the list parameter
        integrationAvailabilityFilter &&
          integrationAvailabilityFilter.forEach(integrationAvailability =>
            params.append('integration_availability', integrationAvailability)
          );

        return {
          url: `/v1/applications?${params.toString()}`,
        };
      },
      transformResponse: (response: ApiListResponseDto<ApplicationDto>) => {
        const internalApplications =
          response.results.map(app => {
            return transformApplication(app);
          }) ?? [];
        return {
          applications: internalApplications,
          total: response.total ?? 0,
        };
      },
    }),
    /**
     * List public+private applications. This is all the applications available
     * to an organization.
     */
    listPrivateApplications: builder.query<
      ListApplicationsResponse,
      ListPrivateApplicationsArgs
    >({
      providesTags: [TagTypes.Application],
      query: arg => {
        return {
          url: `/v1/organizations/${arg.organizationId}/applications`,
          params: omit(arg, 'organizationId'),
        };
      },
      transformResponse: (
        response: ApiListResponseDto<ApplicationDto>
      ): ListApplicationsResponse => {
        return {
          applications:
            response.results.map(app => {
              return transformApplication(app);
            }) ?? [],
          total: response.total ?? 0,
        };
      },
    }),

    createPrivateApplication: builder.mutation<
      CreatePrivateApplicationResponse,
      PrivateApplicationCreationArgs
    >({
      invalidatesTags: [TagTypes.Application],
      query: ({ organizationId, name, url }) => ({
        url: `/v1/organizations/${organizationId}/applications`,
        method: 'POST',
        body: {
          name: name.trim(),
          ...(url?.trim() ? { url: url.trim() } : {}),
        },
      }),
      transformResponse: (response: CreatePrivateApplicationResponseDTo) => {
        return {
          application: transformApplication(response.application),
          organizationApplication: transformOrganizationApplication(
            response.organization_application
          ),
        };
      },
    }),

    getApplication: builder.query<Application, ApplicationFetchingArgs>({
      providesTags: [TagTypes.Application],
      query: ({ applicationKey }) => `/v1/applications/${applicationKey}`,
      transformResponse: (response: ApplicationDto) => {
        return transformApplication(response);
      },
    }),
    createApplication: builder.mutation<Application, ApplicationCreationArgs>({
      invalidatesTags: [TagTypes.Application],
      query: ({ name, slug, url, userManagementUrl, icon, category }) => ({
        url: `/v1/applications`,
        method: 'POST',
        body: {
          name: name.trim(),
          slug,
          url: url?.trim(),
          user_management_url: userManagementUrl?.trim(),
          icon,
          category,
        },
      }),
      transformResponse: (response: ApplicationDto) => {
        return transformApplication(response);
      },
    }),
    updateApplication: builder.mutation<Application, ApplicationUpdateArgs>({
      invalidatesTags: [TagTypes.Application],
      query: ({
        applicationKey,
        name,
        url,
        userManagementUrl,
        icon,
        integrationAvailability,
        category,
      }) => ({
        url: `/v1/applications/${applicationKey}`,
        method: 'PUT',
        body: {
          applicationKey,
          name: name.trim(),
          url: url?.trim(),
          user_management_url: userManagementUrl?.trim(),
          icon,
          integration_availability: integrationAvailability,
          category,
        },
      }),
      transformResponse: (response: ApplicationDto) => {
        return transformApplication(response);
      },
    }),
    uploadIcon: builder.mutation<
      ApplicationIconResponse,
      ApplicationIconUploadArgs
    >({
      query: ({ slug, icon_file }) => {
        const formData = new FormData();
        formData.append('slug', slug);
        formData.append('icon_file', icon_file);
        return {
          url: `/v1/applications/icon`,
          method: 'POST',
          formData: true,
          body: formData,
        };
      },
    }),
    uploadPrivateAppIcon: builder.mutation<
      ApplicationIconResponse,
      PrivateApplicationIconUploadArgs
    >({
      query: ({ slug, organizationId, icon_file }) => {
        const formData = new FormData();
        formData.append('icon_file', icon_file);
        return {
          url: `/v1/organizations/${organizationId}/applications/${slug}/icon`,
          method: 'POST',
          formData: true,
          body: formData,
        };
      },
    }),
    getApplicationUserAccounts: builder.query<
      AppUserAccountsResponse,
      GetAppUserAccountsArgs
    >({
      providesTags: [TagTypes.ApplicationUsers],
      queryFn: async (
        { applicationKey, organizationId, limit },
        queryApi,
        _extraOptions,
        baseQuery
      ) => {
        const response = (await baseQuery({
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/users`,
          params: {
            limit: limit,
          },
        })) as QueryReturnValue<
          AppUserAccountsResponseDto,
          FetchBaseQueryError,
          Record<string, never>
        >;

        if (response.error) {
          return { error: response.error };
        }

        const { data: employees } = await queryApi.dispatch(
          organizationApiSlice.endpoints.getOrganizationEmployees.initiate({
            organizationId,
            status: [
              EmployeeStatusEnum.Active,
              EmployeeStatusEnum.Inactive,
              EmployeeStatusEnum.Unknown,
            ],
          })
        );

        const getEmployee = (id?: string) => {
          if (!id || !employees) {
            return undefined;
          }

          return employees.find(employee => employee.id === id);
        };

        const { result, error, user_accounts } = response.data!;
        const userAccounts = user_accounts?.map(
          ({ email, employee_info, status_in_electric, account_info }) => ({
            email,
            statusInElectric:
              status_in_electric ?? AccountStatusInElectric.UnknownEmail,
            employeeInfo: employee_info
              ? {
                  id: employee_info.id,
                  offboardedAt: employee_info.offboarded_at,
                  employee: getEmployee(employee_info.id),
                }
              : undefined,
            thirdPartyAccountInfo: account_info
              ? {
                  id: account_info.id,
                  createdAt: account_info.created_at,
                  disabled: account_info.disabled,
                  extra: account_info.extra as Record<
                    string,
                    unknown | undefined
                  >,
                }
              : undefined,
          })
        );

        return {
          data: {
            result,
            error,
            userAccounts,
          },
        };
      },
    }),
    getApplicationDevices: builder.query<AppDevicesResponse, GetAppDevicesArgs>(
      {
        query: ({ organizationId, applicationKey }) => ({
          url: `/v1/organizations/${organizationId}/applications/${applicationKey}/devices`,
        }),
        transformResponse: (
          response: AppDevicesResponseDto
        ): AppDevicesResponse => ({
          result: response.result,
          error: response.error,
          devices: response.devices?.map(device => ({
            externalId: device.external_id,
            serialNumber: device.serial_number,
            status: device.status,
            assetInfo: device.asset_info
              ? {
                  id: device.asset_info.id,
                  assignedToEmployeeId:
                    device.asset_info.assigned_to_employee_id,
                  siteInfo: device.asset_info.site_info
                    ? {
                        id: device.asset_info.site_info.id,
                        name: device.asset_info.site_info.name,
                      }
                    : undefined,
                }
              : undefined,
            thirdPartyDeviceInfo: device.device_info
              ? {
                  deviceName: device.device_info.device_name,
                  brand: device.device_info.brand,
                  hardwareModel: device.device_info.hardware_model,
                  osType: device.device_info.os_type,
                  enrolledAt: device.device_info.enrolled_at,
                  lastSeenAt: device.device_info.last_seen_at,
                  assignedUserEmail: device.device_info.assigned_user_email,
                  osVersion: device.device_info.os_version,
                  osName: device.device_info.os_name,
                  extra: device.device_info.extra as
                    | Record<string, unknown>
                    | undefined,
                }
              : undefined,
          })),
        }),
        providesTags: [TagTypes.Application],
      }
    ),
    getMyApplications: builder.query<Application[], string>({
      query: organizationId => ({
        url: `/v1/organizations/${organizationId}/employees/me/applications`,
        params: { has_url: true },
      }),
      transformResponse: (response: PaginatedResponseApplication) =>
        response.results.map(transformApplication),
      providesTags: [TagTypes.Application],
    }),
  }),
});

// export auto-generated hooks
export const {
  useLazyGetApplicationQuery,
  useLazyGetApplicationsQuery,
  useGetApplicationQuery,
  useGetApplicationsQuery,
  useListPrivateApplicationsQuery,
  useCreateApplicationMutation,
  useCreatePrivateApplicationMutation,
  useUpdateApplicationMutation,
  useUploadIconMutation,
  useUploadPrivateAppIconMutation,
  useGetApplicationUserAccountsQuery,
  useLazyGetApplicationUserAccountsQuery,
  useGetApplicationDevicesQuery,
  useLazyGetApplicationDevicesQuery,
  useGetMyApplicationsQuery,
} = applicationApiSlice;
